import { useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  type ApiClient,
  ApiError,
  type HttpMethod,
  type Parser,
  type QueryParams,
} from '../lib/api';
import { useApiClient } from '../lib/use-api-client';
import { type QueryKeys, useQueryKeys } from './keys';

/**
 * Typed request function over the `ApiClient` fetch wrapper. Every hook in `src/api/*` goes
 * through `request<T>()`, so headers (`If-Match`, `Idempotency-Key`), query strings and
 * problem+json handling live in one place.
 */

export type { QueryParams };
export { ApiError };

/** Route-group prefixes relative to `config.apiBaseUrl` (`/api`). */
export const ADMIN_PREFIX = '/admin/v1';
export const PORTAL_PREFIX = '/v1';

export const adminPath = (path: string): string => `${ADMIN_PREFIX}${path}`;
export const portalPath = (path: string): string => `${PORTAL_PREFIX}${path}`;

export interface RequestOptions<T> {
  body?: unknown;
  query?: QueryParams;
  /** Validates/narrows the JSON response (any `{ parse }` object, e.g. a Zod schema). */
  parser?: Parser<T>;
  /** Sent as `If-Match` (project `configVersion`, ticket `version`). */
  ifMatch?: string | number;
  /** Sent as `Idempotency-Key`; the API de-duplicates POSTs carrying the same key. */
  idempotencyKey?: string;
  signal?: AbortSignal;
}

export type Request = <T>(
  method: HttpMethod,
  path: string,
  options?: RequestOptions<T>,
) => Promise<T>;

export function createRequest(client: ApiClient): Request {
  return <T>(method: HttpMethod, path: string, options: RequestOptions<T> = {}) => {
    const headers: Record<string, string> = {};
    if (options.ifMatch !== undefined) headers['If-Match'] = String(options.ifMatch);
    if (options.idempotencyKey !== undefined) headers['Idempotency-Key'] = options.idempotencyKey;
    return client.request<T>(method, path, {
      body: options.body,
      query: options.query,
      headers,
      signal: options.signal,
      schema: options.parser,
    });
  };
}

/** `request<T>()` bound to the current config + session. Stable across renders. */
export function useRequest(): Request {
  const client = useApiClient();
  return useMemo(() => createRequest(client), [client]);
}

/** Portal sub-resource lists (`transitions`, `watchers`, `links`) come back as `{ items }`. */
export interface ItemsResponse<T> {
  items: T[];
}

/** 409 (`conflict` / `version_mismatch`) or 412 (`precondition_failed`): reload and retry. */
export function isConflictError(error: unknown): error is ApiError {
  return error instanceof ApiError && (error.status === 409 || error.status === 412);
}

export function isNotFoundError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 404;
}

export function isForbiddenError(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 403;
}

/** Everything a resource hook file needs: request function, tenant-scoped keys, invalidation. */
export interface ApiContext {
  request: Request;
  keys: QueryKeys;
  /** Invalidates every listed key prefix (tenant-namespaced) after a mutation. */
  invalidate: (...keys: readonly (readonly unknown[])[]) => Promise<void>;
  /** Drops cached data for the listed key prefixes (e.g. after a delete). */
  remove: (...keys: readonly (readonly unknown[])[]) => void;
}

export function useApiContext(): ApiContext {
  const request = useRequest();
  const keys = useQueryKeys();
  const queryClient = useQueryClient();
  return useMemo(
    () => ({
      request,
      keys,
      invalidate: async (...targets) => {
        await Promise.all(
          targets.map((queryKey) => queryClient.invalidateQueries({ queryKey: [...queryKey] })),
        );
      },
      remove: (...targets) => {
        for (const queryKey of targets) queryClient.removeQueries({ queryKey: [...queryKey] });
      },
    }),
    [request, keys, queryClient],
  );
}
